import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  Typography,
  Box,
  Divider,
  Card,
  TextField,
  FormHelperText,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const Declaration = ({
  formData,
  setFormData,
  errors,
  setDeclarationErrors,
  commonError,
}) => {
  const [pdfOpen, setPdfOpen] = useState(false);

  // Helper function to convert string values to boolean for checkboxes
  const getCheckboxValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === '1' || value === 1) return true;
    if (value === 'false' || value === '0' || value === 0 || value === null || value === undefined) return false;
    return Boolean(value);
  };

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set current date when component mounts or when form data changes
  React.useEffect(() => {
    if (!formData.declaration_date) {
      setFormData(prev => ({
        ...prev,
        declaration_date: getCurrentDate()
      }));
    }
  }, [formData.declaration_date, setFormData]);

  // Clear specific error when field changes
  const clearError = (fieldName) => {
    if (errors && errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      delete newErrors.commonError; // Also clear common error
      setDeclarationErrors(newErrors);
    }
  };

  // Handle checkbox change and open PDF
  const handleDeclareResponsibleChange = (e) => {
    const isChecked = e.target.checked;

    setFormData({
      ...formData,
      declare_responsible_ai: isChecked,
    });

    // Open PDF when checkbox is checked
    if (isChecked) {
      setPdfOpen(true);
    }

    clearError('declare_responsible_ai');
  };

  // Simple handlers for other fields
  const handleEthicalOriginalChange = (e) => {
    setFormData({
      ...formData,
      ethical_original_licensed: e.target.checked,
    });
    clearError('ethical_original_licensed');
  };

  const handleEthicalNoHarmfulChange = (e) => {
    setFormData({
      ...formData,
      ethical_no_harmful_content: e.target.checked,
    });
    clearError('ethical_no_harmful_content');
  };

  const handleEthicalConsentChange = (e) => {
    setFormData({
      ...formData,
      ethical_consent_obtained: e.target.checked,
    });
    clearError('ethical_consent_obtained');
  };

  const handleEthicalResponsibleChange = (e) => {
    setFormData({
      ...formData,
      ethical_responsible_ai: e.target.checked,
    });
    clearError('ethical_responsible_ai');
  };

  const handleDeclareOriginalChange = (e) => {
    setFormData({
      ...formData,
      declare_original_work: e.target.checked,
    });
    clearError('declare_original_work');
  };

  const handleDeclareAiDisclosureChange = (e) => {
    setFormData({
      ...formData,
      declare_ai_disclosure: e.target.checked,
    });
    clearError('declare_ai_disclosure');
  };

  const handleDeclareScreeningChange = (e) => {
    setFormData({
      ...formData,
      declare_screening_rights: e.target.checked,
    });
    clearError('declare_screening_rights');
  };

  const handleConfirmChange = (e) => {
    setFormData({
      ...formData,
      confirm: e.target.checked,
    });
    clearError('confirm');
  };

  const handleDigitalSignatureChange = (e) => {
    setFormData({
      ...formData,
      digital_signature: e.target.value,
    });
    clearError('digital_signature');
  };

  const handleDeclarationPlaceChange = (e) => {
    setFormData({
      ...formData,
      declaration_place: e.target.value,
    });
    clearError('declaration_place');
  };

  // Close PDF modal
  const handleClosePdf = () => {
    setPdfOpen(false);
  };

  // Replace this URL with your actual PDF URL
  const pdfUrl = "/festival1/annexure-a.pdf"; // Update this path to your actual PDF file

  return (
    <div className="container mt-3">
      <div className="cmot-phone">
        <h5>STEP 5</h5>
        <p>
          Compliance & Declaration <span className="text-danger">*</span>
        </p>
      </div>

      <form>
        {/* Ethical Compliance & Transparency Checklist */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, color: "#1c5768", fontWeight: "bold" }}
          >
            Ethical Compliance & Transparency Checklist
          </Typography>

          <Card
            sx={{
              p: 3,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
            }}
          >
            <FormGroup>
              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.ethical_original_licensed)}
                    onChange={handleEthicalOriginalChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="All AI-generated content is original or properly licensed."
              />
              {errors.ethical_original_licensed && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.ethical_original_licensed}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.ethical_no_harmful_content)}
                    onChange={handleEthicalNoHarmfulChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="No copyrighted, harmful, or misleading material has been used."
              />
              {errors.ethical_no_harmful_content && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.ethical_no_harmful_content}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.ethical_consent_obtained)}
                    onChange={handleEthicalConsentChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="Consent obtained for any real likeness, voices, or data used."
              />
              {errors.ethical_consent_obtained && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.ethical_consent_obtained}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.ethical_responsible_ai)}
                    onChange={handleEthicalResponsibleChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="The work upholds Responsible and Ethical AI Storytelling Practices (Refer Annexure A)"
              />
              {errors.ethical_responsible_ai && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.ethical_responsible_ai}
                </FormHelperText>
              )}
            </FormGroup>
          </Card>
        </Box>

        {/* Declaration Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, color: "#1c5768", fontWeight: "bold" }}
          >
            Declaration
          </Typography>

          <Typography variant="body1" sx={{ mb: 1, fontStyle: "italic" }}>
            I/We confirm that:
          </Typography>

          <Card sx={{ p: 3, border: "1px solid #e0e0e0" }}>
            <FormGroup>
              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.declare_original_work)}
                    onChange={handleDeclareOriginalChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="The submitted work is original and created by our team."
              />
              {errors.declare_original_work && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.declare_original_work}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.declare_ai_disclosure)}
                    onChange={handleDeclareAiDisclosureChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="All AI tools and creative processes have been accurately disclosed."
              />
              {errors.declare_ai_disclosure && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.declare_ai_disclosure}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.declare_responsible_ai)}
                    onChange={handleDeclareResponsibleChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="The film aligns with Responsible AI Storytelling Practices (Refer Annexure A)."
              />
              {errors.declare_responsible_ai && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.declare_responsible_ai}
                </FormHelperText>
              )}

              <FormControlLabel
                required
                control={
                  <Checkbox
                    checked={getCheckboxValue(formData.declare_screening_rights)}
                    onChange={handleDeclareScreeningChange}
                    style={{ color: "#2C3E50" }}
                    className="checkboxcmot"
                  />
                }
                label="LTIM, NFDC, and IFFI may screen and promote our work as part of the festival and related showcases."
              />
              {errors.declare_screening_rights && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {errors.declare_screening_rights}
                </FormHelperText>
              )}
            </FormGroup>

            {/* Signature and Date Fields */}
            <Grid container spacing={2} className="mt-4">
              <Grid item xs={12} sm={12} md={6} lg={12}>
                <TextField
                  fullWidth
                  label="Name / Signature (Digital Accepted)"
                  variant="outlined"
                  value={formData.digital_signature || ""}
                  onChange={handleDigitalSignatureChange}
                  required
                  placeholder="Enter your name or digital signature"
                  error={!!errors.digital_signature}
                  helperText={errors.digital_signature}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="text"
                  variant="outlined"
                  value={formData.declaration_date || getCurrentDate()}
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Auto-filled with current date"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Place"
                  variant="outlined"
                  value={formData.declaration_place || ""}
                  onChange={handleDeclarationPlaceChange}
                  required
                  placeholder="Enter place"
                  error={!!errors.declaration_place}
                  helperText={errors.declaration_place}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={12} className="mt-3">
                <FormGroup>
                  <FormControlLabel
                    required
                    control={
                      <Checkbox
                        checked={getCheckboxValue(formData.confirm)}
                        onChange={handleConfirmChange}
                        style={{ color: "#2C3E50" }}
                        className="checkboxcmot"
                      />
                    }
                    label="I confirm that all the information provided above is true and accurate."
                  />
                  {errors.confirm && (
                    <FormHelperText error sx={{ ml: 4 }}>
                      {errors.confirm}
                    </FormHelperText>
                  )}
                </FormGroup>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {errors.commonError && (
          <Grid item xs={12}>
            <p className="text-danger">{errors.commonError}</p>
          </Grid>
        )}
      </form>

      {/* PDF Modal */}
      <Dialog
        open={pdfOpen}
        onClose={handleClosePdf}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            Annexure A - Responsible AI Storytelling Practices
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClosePdf}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title="Annexure A - Responsible AI Storytelling Practices"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Declaration;